import { useCallback, useContext, useMemo } from 'react';

import { BFFContext } from '../context';

export interface UseGenericCacheProps {
	accountId?: string;
	cloudId?: string;
	locale?: string;
	orgId?: string;
	productType: string;
	subProduct?: string;
}

/**
 * Generic cache hook that can work with any type T
 * @template T - The type of objects to be cached
 * @returns Object containing cache operations and current cached object
 */
export function useGenericCache<T>({
	cloudId,
	productType,
	orgId,
	accountId,
	subProduct,
	locale,
}: UseGenericCacheProps) {
	const {
		value: { consumer, cache },
		setValue,
	} = useContext(BFFContext);

	if (!consumer) {
		throw new Error('BFFContext Consumer is not set');
	}

	// Compute cache key using useMemo instead of useEffect and setCacheKey
	const cacheKey = useMemo(() => {
		const base = `gc-${consumer}-${orgId ?? 'NO_ORG_ID'}:${cloudId ?? 'NO_CLOUD_ID'}:${
			accountId ?? 'NO_ACCOUNT_ID'
		}:${productType}`;
		const sub = subProduct ? `:${subProduct}` : '';
		const loc = locale ? `:${locale}` : '';
		return `${base}${sub}${loc}`;
	}, [consumer, orgId, cloudId, accountId, productType, subProduct, locale]);

	const cachedObject: T | undefined = cache[cacheKey];

	const setCachedObject = useCallback(
		(objectToCache: T) => {
			setValue((prev) => ({
				...prev,
				cache: {
					...prev.cache,
					[cacheKey]: objectToCache,
				},
			}));
		},
		[cacheKey, setValue],
	);

	const removeCachedObject = useCallback(() => {
		setValue((prev) => {
			const { [cacheKey]: removed, ...remainingCache } = prev.cache;
			return {
				...prev,
				cache: remainingCache,
			};
		});
	}, [cacheKey, setValue]);

	const getCachedObject = (targetCacheKey: string): T | undefined => cache[targetCacheKey];

	const hasCachedObject = (targetCacheKey: string): boolean => cache[targetCacheKey] !== undefined;

	return {
		setCachedObject,
		removeCachedObject,
		getCachedObject,
		hasCachedObject,
		cacheKey,
		cachedObject,
	};
}
